import { useState, useCallback } from 'react'
import { deserializeMessagesFromStorage, serializeMessagesForStorage } from '../utils/componentMapping'

const useChatSessionManager = (api_url, userId) => {
  const [chatSessions, setChatSessions] = useState([])
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [messages, setMessages] = useState([])
  const [sessionName, setSessionName] = useState('Untitled Session')

  const fetchChatSessions = async () => {
    try {
      const response = await fetch(`${api_url}/api/chat-history/${userId}`)
      if (!response.ok) throw new Error('Failed to fetch chat sessions')
      const data = await response.json()
      setChatSessions(data)
    } catch (error) {
      console.error('Error fetching chat sessions:', error)
    }
  }

  const saveCurrentSession = async (new_name = '', searchId = null, roleType = null, discoveryState = null, componentData = null) => {
    // If userId is not provided, don't save the session
    if (!userId) {
      console.log('🚫 Cannot save session: userId is not provided');
      return null;
    }
    
    // 🎯 CRITICAL FIX: Ensure session name is never "Untitled Session" when saving
    const name = new_name || sessionName || `Session ${Date.now()}`
    try {
      // Only skip saving if we have no messages at all (not even initial messages)
      if (!messages || messages.length === 0) {
        console.log('Cannot save session: no messages to save');
        return null;
      }
      
      console.log(`🔍 Attempting to save session - currentSessionId: ${currentSessionId}, messages: ${messages.length}, name: ${name}`);
      
      const endpoint = currentSessionId
        ? `${api_url}/api/chat-history/${userId}/${currentSessionId}`
        : `${api_url}/api/chat-history/${userId}`

      const method = currentSessionId ? 'PUT' : 'POST'
      
      // Serialize components in messages before saving to backend
      const serializedMessages = serializeMessagesForStorage(messages)
      
      console.log(`📦 Serialized ${messages.length} messages, ${serializedMessages.length} after serialization`);
      console.log(`🔧 Messages with components:`, messages.filter(msg => msg.component || msg._hasComponent).map(msg => ({
        role: msg.role,
        component: msg.component,
        hasComponent: msg._hasComponent,
        content: msg.content.substring(0, 50) + '...'
      })));
      
      const body = JSON.stringify({ 
        name, 
        messages: serializedMessages, 
        searchId,
        roleType,
        discoveryState,
        componentData
      })

      console.log(`🌐 Saving session to ${endpoint} (${method}) with searchId: ${searchId}, roleType: ${roleType}, discoveryState: ${JSON.stringify(discoveryState)}, componentData: ${componentData ? 'present' : 'null'}`)
      
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body
      })

      if (!response.ok) throw new Error('Failed to save chat session')

      let savedSession = null;
      
      if (!currentSessionId) {
        const newSession = await response.json()
        setCurrentSessionId(newSession.history.id)
        savedSession = {
          ...newSession.history,
          userId
        };
      } else {
        // For existing sessions, get the updated session
        const sessionResponse = await fetch(`${api_url}/api/chat-history/${userId}/${currentSessionId}`)
        if (sessionResponse.ok) {
          savedSession = await sessionResponse.json()
        }
      }

      fetchChatSessions()
      
      // Return the saved session so it can be used by the caller
      return savedSession;
    } catch (error) {
      console.error('Error saving chat session:', error)
      return null;
    }
  }

  const loadChatSession = useCallback(async sessionId => {
    try {
      const response = await fetch(
        `${api_url}/api/chat-history/${userId}/${sessionId}`
      )
      if (!response.ok) throw new Error('Failed to load chat session')

      const session = await response.json()
      setCurrentSessionId(sessionId)
      
      // Deserialize components in messages when loading from database
      const deserializedMessages = deserializeMessagesFromStorage(session.messages || [])
      setMessages(deserializedMessages)
      setSessionName(session.name)
    } catch (error) {
      console.error('Error loading chat session:', error)
    }
  }, [api_url, userId])

  const deleteChatSession = async sessionId => {
    try {
      const response = await fetch(
        `${api_url}/api/chat-history/${userId}/${sessionId}`,
        {
          method: 'DELETE'
        }
      )
      if (!response.ok) throw new Error('Failed to delete chat session')
      fetchChatSessions()
    } catch (error) {
      console.error('Error deleting chat session:', error)
    }
  }

  const startNewSession = () => {
    setCurrentSessionId(null)
    setMessages([])
    setSessionName('Untitled Session')
  }

  return {
    chatSessions,
    currentSessionId,
    messages,
    sessionName,
    fetchChatSessions,
    saveCurrentSession,
    loadChatSession,
    deleteChatSession,
    startNewSession,
    setMessages,
    setSessionName,
  }
}

export default useChatSessionManager
